import React, { useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { groupBy, uniqueId } from 'lodash'
import { useNavigation } from '@react-navigation/core'
import moment from 'moment'
import { useFocusEffect, useRoute } from '@react-navigation/native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { DeviceEventEmitter, View } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Timeline } from '@src/components/Timeline/Timeline'
import { Feature, useFeatureFlag } from '@components'
import {
  eventsForDateSelector,
  eventsForDatesSelector,
  eventsSelector,
} from '@src/selectors/events'
import { EventsList } from '@src/screens/Events/components/EventsList'
import { OCCURRED_AT } from '@src/containers/ListFilter/utils'
import { AccountAvatarNavigationBar } from '@src/components/navigationBar/AccountAvatarNavigationBar'
import { useChartInteractions } from '@src/screens/Home/hooks/useChartInteractions'
import { DATE_FORMAT, MONTH_NAME_AND_DATE_WITH_YEAR_FORMAT } from '@src/config/momentFormat'
import { useDispatchAsync } from '@src/utils'
import { useHasLargeScreen } from '@src/config/device'
import { Button, IconButton, Text } from '@components/base'
import { useIntercomMessageNavigationAccessory } from '@src/hooks/useIntercomMessageNavigationAccessory'
import { useNotificationInboxNavigationAccessory } from '@src/hooks/useNotificationInboxNavigationAccessory'
import { useRefreshNotificationsOnFocus } from '@src/hooks/useRefreshNotificationsOnFocus'
import { AppRouteProp } from '@src/navigation/types'
import { eventsChartsSelector } from '@src/models/dailyCharts/selectors'
import {
  calendarDateSelector,
  measurementTypeDefLookupSelector,
  pendingEventsRefreshSelector,
} from '@src/selectors/app'
import { dashboardGraphSelector, preferredHeroChartSelector } from '@src/selectors/settings'
import { NotificationBanner } from '@src/components/notifications/NotificationBanner'
import { LiveSurfaceBanner } from '@src/components/notifications/banners/LiveSurfaceBanner'
import { NotificationEngineScreen } from '@src/types'
import { InformationPopups } from '@src/components/InformationPopups'
import { useSyncHealthConnectData } from '../Events/utils/useSyncHealthConnectData'
import { EVENTS_DEFAULT_SORT_STATE, MAX_CHART_DAY_RANGE } from '../Events/models/constants'
import { EventsItemType } from '../Events/models/events.types'
import { buildSections } from '../Events/utils/utils'
import { DashboardGraph, HeroChartType } from '../Settings/models/settings.types'
import { HighlightsSection } from '../Events/components/Highlights/HighlightsSection'
import { StressManagementBanner } from '../NutritionistHub'
import { useGetByosVideoCallUpsellNextStep, useOnboardingModals } from './hooks'
import { DialChart } from './components/HeroChart/DialChart/DialChart'
import { NutritionSummaryChart } from './components/HeroChart/NutritionSummaryChart/NutritionSummaryChart'
import { CustomizeChartButton } from './components/HeroChart/components/CustomizeChartButton'
import { FreeConsultationChatlikeBanner } from './components/Banners/FreeConsultationChatlikeBanner'
import { NutritionCoachingChatlikeBanner } from './components/Banners/NutritionCoachingChatlikeBanner'
import { OnboardingChecklistBanner } from './components/Banners/OnboardingChecklistBanner'
import { VideoCallsUpsellBanner } from './components/Banners/VideoCallsUpsellBanner'
import { ByosVideoCallsUpsellStep } from './constants'
import { FreeCallTrialUpsellBanner } from './components/Banners/FreeCallTrialUpsellBanner'
import { FreeConsultationUpsellCard } from './components/Banners/FreeConsultationUpsellCard'
import { NutritionGuidanceUpsellCard } from './components/Banners/NutritionGuidanceUpsellCard'
import { ReferralBanner } from './components/Banners/ReferralBanner'
import { UhcOnboardingBanner } from './components/Banners/UhcOnboardingBanner'
import { UpgradeSubscriptionBanner } from './components/Banners/UpgradeSubscriptionBanner'
import { MyPlan } from './components/MyPlan/MyPlan'
import { EmptyEventsPlaceholder } from './components/EmptyEventsPlaceholder'
import { HomeActionButton } from './components/HomeActionButton'
import { shouldShowByosVideoCallsUpsellSurface } from './utils'

const filter = { scope: 'events' }
export const PLUS_MENU_BOTTOM_MARGIN = 14

export const HomeScreen = () => {
  const dispatch = useDispatch()
  const dispatchAsync = useDispatchAsync()
  const navigation = useNavigation()
  const hasLargeScreen = useHasLargeScreen()
  const insets = useSafeAreaInsets()
  const styles = useStyleSheet(themedStyle)
  const syncHealthConnectData = useSyncHealthConnectData()
  const intercomNavigationAccessory = useIntercomMessageNavigationAccessory({ source: 'Home' })
  const notificationInboxAccessory = useNotificationInboxNavigationAccessory()
  const rightAccessories = [...intercomNavigationAccessory, ...notificationInboxAccessory]

  useRefreshNotificationsOnFocus()

  const heroChart = useSelector(preferredHeroChartSelector)

  useOnboardingModals()
  const nextByosVideoCallsUpsellStep = useGetByosVideoCallUpsellNextStep()

  const route = useRoute<AppRouteProp<'Home'>>()
  const resetFilters = route.params?.resetFilters
  const isDataLoading = useRef(false)
  const { sort, filters } = useSelector(eventsSelector)
  const items = useSelector(eventsForDatesSelector)
  const calendar = useSelector(calendarDateSelector)
  const dashboardGraph = useSelector(dashboardGraphSelector)

  const isKetoneChartIncluded = dashboardGraph === DashboardGraph.Combined

  useFocusEffect(
    useCallback(() => {
      if (resetFilters) {
        navigation.setParams({ resetFilters: undefined }) // make sure it runs only once

        const today = moment().format(DATE_FORMAT)
        dispatch({
          type: 'app/changeDateRange',
          payload: {
            startDate: today,
            endDate: today,
          },
        })
        dispatch({
          type: 'events/updateSort',
          payload: {
            ...EVENTS_DEFAULT_SORT_STATE,
          },
        })
      }
      syncHealthConnectData()
    }, [dispatch, navigation, resetFilters, syncHealthConnectData]),
  )

  const refreshData = useCallback(
    async (useCache: boolean) => {
      if (isDataLoading.current) {
        return
      }

      const promises = [
        dispatchAsync({
          type: 'users/fetch',
        }),
        dispatchAsync({
          type: 'dailyNutrition/fetch',
          useCache,
        }),
        dispatchAsync({
          type: 'dailyCharts/fetch',
          useCache,
        }),
        dispatchAsync({
          type: 'nutritionistHub/fetchAppointmentRecurrence',
        }),
        dispatchAsync({
          type: 'nutritionistHub/fetchCoachAssignments',
        }),
        dispatchAsync({
          type: 'nutritionistHub/fetchInsurancePolicy',
        }),
        dispatchAsync({
          type: 'nutritionistHub/fetchPendingEhrAppointments',
        }),
        dispatchAsync({
          type: 'nutritionistHub/fetchAppointmentInfo',
        }),
      ]

      isDataLoading.current = true

      try {
        await Promise.all(promises)
      } catch {
        // do nothing
      } finally {
        isDataLoading.current = false
      }
    },
    [dispatchAsync],
  )

  const loadDataOnFocus = useCallback(() => {
    refreshData(true)
  }, [refreshData])

  useFocusEffect(loadDataOnFocus)

  const pendingEventsRefresh = useSelector(pendingEventsRefreshSelector)

  // Unlike the nutrition summary, the events list is a focus-unaware DataProviderList:
  // it only reloads on mount, filter/sort changes, or an 'onRefreshPress' broadcast, so
  // it misses anything logged off-screen (e.g. via Nora's action tunnels). Drop the
  // cached pages first, otherwise the reload just re-serves them for past-dated logs.
  useFocusEffect(
    useCallback(() => {
      if (pendingEventsRefresh) {
        dispatch({ type: 'app/setPendingEventsRefresh', payload: false })
        dispatch({ type: 'events/cacheClear', payload: { matchName: 'events/fetch' } })
        DeviceEventEmitter.emit('onRefreshPress')
      }
    }, [pendingEventsRefresh, dispatch]),
  )

  const measurementTypeDefLookup = useSelector(measurementTypeDefLookupSelector)
  const moveDailyMeasurementsToEnd = useFeatureFlag(Feature.MoveDailyMeasurementsToEnd)
  const notificationInboxEnabled = useFeatureFlag(Feature.NotificationInbox)
  const noraVoiceModeEnabled = useFeatureFlag(Feature.NoraRealtimeVoiceMode)

  const seeAllEventsButton = useMemo(
    () => (
      <Button
        type="secondary"
        size="small"
        style={styles.allEventsButton}
        onPress={() => navigation.navigate('EventsListScreen', { resetFilters: false })}
        accessibilityLabel="See all events"
      >
        See All
      </Button>
    ),
    [navigation, styles.allEventsButton],
  )

  const allEventsAdornment = useMemo(
    () => <View style={styles.allEventsAdornment}>{seeAllEventsButton}</View>,
    [seeAllEventsButton, styles.allEventsAdornment],
  )

  const sections = useMemo(
    () =>
      buildSections({
        items,
        measurementTypeDefLookup,
        moveDailyMeasurementsToEnd,
        firstSectionAdornment: allEventsAdornment,
      }),
    [allEventsAdornment, items, measurementTypeDefLookup, moveDailyMeasurementsToEnd],
  )

  const groupedEvents: any[] = useMemo(() => {
    const dateGroups = groupBy(items, (i) => moment(i.occurredAt).format(DATE_FORMAT))
    return Object.entries(dateGroups).map(([date, data], index) => ({
      id: uniqueId(date),
      index,
      title: moment(date).format(MONTH_NAME_AND_DATE_WITH_YEAR_FORMAT),
      occurredAt: moment(date),
      data,
    }))
  }, [items])

  const {
    listRef,
    primaryChartRef,
    secondaryChartRef,
    highlightedIndex,
    onChartLoadEnd,
    onChartHover,
    onEventListViewableItemsChanged,
    onEventListDragStart,
    onEventListMomentumScrollEnd,
  } = useChartInteractions(groupedEvents)

  const chartComponent = useMemo(() => {
    const onCustomizeHomeScreenPress = () => {
      navigation.navigate('CustomizeHomeModal')
    }

    const customizeHeaderButton = <CustomizeChartButton onPress={onCustomizeHomeScreenPress} />

    const displayDateRangeWarning =
      moment(calendar.endDate).diff(moment(calendar.startDate), 'days') > MAX_CHART_DAY_RANGE

    const timelineChartComponent = (
      <Timeline
        primaryChartRef={primaryChartRef}
        secondaryChartRef={secondaryChartRef}
        onChartHover={onChartHover}
        onChartLoadEnd={onChartLoadEnd}
        hideMultiSlider
        key={`${isKetoneChartIncluded}`}
        isKetoneChartIncluded={isKetoneChartIncluded}
        displayGlucoseRangeIcon={false}
        displayDateRangeWarning={displayDateRangeWarning}
        headerRightAdornment={customizeHeaderButton}
        chartsSelector={eventsChartsSelector<EventsItemType>(eventsForDateSelector)}
      />
    )

    const nutritionChartComponent = (
      <>
        <View style={styles.chartHeader}>
          <Text type="large" bold lineSpacing="none">
            Nutrition Summary
          </Text>
          <View style={styles.customizeHomeSection}>{customizeHeaderButton}</View>
        </View>
        <View style={styles.chartContainer}>
          <NutritionSummaryChart />
        </View>
      </>
    )

    const dialChartComponent = (
      <>
        <View style={styles.chartHeader}>
          <Text type="large" bold lineSpacing="none">
            Latest Glucose Data
          </Text>
          <View style={styles.customizeHomeSection}>{customizeHeaderButton}</View>
        </View>
        <View style={styles.chartContainer}>
          <DialChart />
        </View>
      </>
    )

    const emptyChartComponent = (
      <View style={styles.emptyChartSection}>
        <CustomizeChartButton onPress={onCustomizeHomeScreenPress} text="Customize Home Screen" />
      </View>
    )

    switch (heroChart) {
      case HeroChartType.NutritionSummaryChart:
        return nutritionChartComponent
      case HeroChartType.DialChart:
        return dialChartComponent
      case HeroChartType.NoChart:
        return emptyChartComponent
      default:
        return timelineChartComponent
    }
  }, [
    calendar.endDate,
    calendar.startDate,
    heroChart,
    isKetoneChartIncluded,
    navigation,
    onChartHover,
    onChartLoadEnd,
    primaryChartRef,
    secondaryChartRef,
    styles.chartContainer,
    styles.chartHeader,
    styles.customizeHomeSection,
    styles.emptyChartSection,
  ])

  const sortOrderBy = sort.orderBy

  const pagination = useMemo(() => ({ type: sortOrderBy === OCCURRED_AT ? 'date' : 'page' }), [
    sortOrderBy,
  ])

  const onOpenMenuPress = useCallback(() => {
    navigation.navigate('ShowActions')
  }, [navigation])

  const openMenuStyles = useMemo(
    () => [
      styles.toggle,
      { bottom: hasLargeScreen && insets.bottom ? insets.bottom : PLUS_MENU_BOTTOM_MARGIN },
    ],
    [hasLargeScreen, insets.bottom, styles.toggle],
  )

  const listHeaderComponent = useMemo(() => {
    const headerComponent = (
      <View style={styles.listHeader}>
        <>
          <LiveSurfaceBanner screen={NotificationEngineScreen.Home} />
          <InformationPopups />
          <UpgradeSubscriptionBanner />
          {shouldShowByosVideoCallsUpsellSurface({
            notificationInboxEnabled,
            nextByosVideoCallsUpsellStep,
            step: ByosVideoCallsUpsellStep.ActionItem,
          }) && <NutritionGuidanceUpsellCard />}
          {shouldShowByosVideoCallsUpsellSurface({
            notificationInboxEnabled,
            nextByosVideoCallsUpsellStep,
            step: ByosVideoCallsUpsellStep.FreeConsultationActionItem,
          }) && <FreeConsultationUpsellCard />}
          <UhcOnboardingBanner style={styles.onboardingBanner} />
          <StressManagementBanner style={styles.onboardingBanner} />
          <ReferralBanner style={styles.onboardingBanner} />
          <FreeCallTrialUpsellBanner style={styles.onboardingBanner} />
          <MyPlan />
        </>
        <HighlightsSection />
      </View>
    )
    return headerComponent
  }, [
    nextByosVideoCallsUpsellStep,
    notificationInboxEnabled,
    styles.listHeader,
    styles.onboardingBanner,
  ])

  const listFooterComponent = useMemo(() => {
    return (
      <View style={styles.listFooterContainer}>
        <Text type="regular" style={styles.listFooterText}>
          View full log history
        </Text>
        {seeAllEventsButton}
      </View>
    )
  }, [seeAllEventsButton, styles.listFooterContainer, styles.listFooterText])

  const emptyViewPlaceholder = useMemo(
    () => <EmptyEventsPlaceholder footer={seeAllEventsButton} />,
    [seeAllEventsButton],
  )

  const banners = (
    <>
      <NotificationBanner screen={NotificationEngineScreen.Home} />
      {shouldShowByosVideoCallsUpsellSurface({
        notificationInboxEnabled,
        nextByosVideoCallsUpsellStep,
        step: ByosVideoCallsUpsellStep.ChatlikeNotification,
      }) && <NutritionCoachingChatlikeBanner />}
      {shouldShowByosVideoCallsUpsellSurface({
        notificationInboxEnabled,
        nextByosVideoCallsUpsellStep,
        step: ByosVideoCallsUpsellStep.FreeConsultationChatlikeNotification,
      }) && <FreeConsultationChatlikeBanner />}
      <OnboardingChecklistBanner />

      <VideoCallsUpsellBanner />
    </>
  )

  return (
    <AccountAvatarNavigationBar rightAccessories={rightAccessories}>
      {banners}
      <EventsList
        ChartComponent={chartComponent}
        listRef={listRef}
        items={sections}
        name="events"
        dataPath="events.events"
        filter={filter}
        sort={sort}
        pagination={pagination}
        useCache
        onDataLoadStart={refreshData}
        filters={filters}
        highlightedIndex={highlightedIndex}
        calendar={calendar}
        onViewableItemsChanged={onEventListViewableItemsChanged}
        onDragStart={onEventListDragStart}
        onMomentumScrollEnd={onEventListMomentumScrollEnd}
        finishedLoadingFooterComponent={listFooterComponent}
        showAllEventsButton
        collapseChartOnScroll={false}
        headerComponent={listHeaderComponent}
        emptyViewPlaceholder={emptyViewPlaceholder}
        showEventsListFilters={false}
        showSearchBar={false}
      />
      {noraVoiceModeEnabled ? (
        <HomeActionButton style={openMenuStyles} onOpenMenu={onOpenMenuPress} />
      ) : (
        <IconButton
          accessibilityLabel="Open Menu"
          iconName="plus"
          size="l"
          style={openMenuStyles}
          type="primary"
          onPress={onOpenMenuPress}
        />
      )}
    </AccountAvatarNavigationBar>
  )
}

const themedStyle = StyleService.create({
  toggle: {
    position: 'absolute',
    right: 20,
  },
  customizeHomeSection: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginHorizontal: 8,
  },
  emptyChartSection: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  chartContainer: {
    marginHorizontal: 16,
    paddingVertical: 8,
    paddingLeft: 0,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'theme.surface.base',
    overflow: 'hidden',
  },
  chartHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginHorizontal: 16,
    marginBottom: 8,
    marginTop: 8,
  },
  allEventsButton: {
    backgroundColor: 'theme.surface.base2',
  },
  allEventsAdornment: {
    paddingBottom: 8,
  },
  listHeader: {
    paddingBottom: 16,
  },
  onboardingBanner: {
    marginHorizontal: 16,
    marginTop: 16,
    marginBottom: 8,
  },
  listFooterContainer: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  listFooterText: {
    marginBottom: 8,
    color: 'theme.text.tertiary',
  },
})
